import { defineStore } from 'pinia';
import { ref } from 'vue';

import { clearRumUser, identifyRumUser } from '@/plugins/datadog';
import { fetchMe } from '@/services/meService';
import { useAuthStore } from '@/store/authStore';
import type { Me } from '@/types/access';
import { extractErrorMessage } from '@/utils/apiError';

// Identify once, here: this is the only point that holds the whole picture — the sub from
// the auth store plus the authoritative email and role from /me.
function identifyFromMe(me: Me): void {
  const user = useAuthStore().user;
  // No sub means the auth store hasn't been populated yet (e.g. a guard-triggered /me
  // racing App.vue's bootstrap) — skip rather than identify a session with an empty id.
  if (!user?.sub) return;
  identifyRumUser({ sub: user.sub, name: user.name, email: me.email, role: me.role ?? 'none' });
}

export const useAccessStore = defineStore('access', () => {
  const me = ref<Me | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const loaded = ref(false);

  let inFlight: Promise<void> | null = null;
  let epoch = 0;

  async function fetchAndStore(): Promise<void> {
    const requestEpoch = epoch;
    loading.value = true;
    error.value = null;
    try {
      const result = await fetchMe();
      if (requestEpoch !== epoch) return;
      me.value = result;
      loaded.value = true;
      identifyFromMe(result);
    } catch (loadError) {
      if (requestEpoch !== epoch) return;
      error.value = extractErrorMessage(loadError);
    } finally {
      if (requestEpoch === epoch) {
        loading.value = false;
      }
    }
  }

  function load(): Promise<void> {
    if (loaded.value && !error.value) return Promise.resolve();
    if (inFlight) return inFlight;
    const requestEpoch = epoch;
    inFlight = fetchAndStore().finally(() => {
      // Only clear inFlight if a reset() (or another load() started after one)
      // hasn't already moved us to a new epoch — otherwise this stale
      // callback would clobber a newer in-flight request's reference.
      if (requestEpoch === epoch) {
        inFlight = null;
      }
    });
    return inFlight;
  }

  function reset(): void {
    epoch += 1;
    inFlight = null;
    me.value = null;
    loading.value = false;
    error.value = null;
    loaded.value = false;
    clearRumUser();
  }

  function fail(message: string): void {
    error.value = message;
    loading.value = false;
  }

  return { me, loading, error, loaded, load, reset, fail };
});
